import type { FormItem, FormItemDefinition, FormSectionRes, TemplateSection } from 'types';
import type { LocalFormItem, LocalFormItemData, LocalFormItemTab, LocalSection, TabSection, WithTab } from 'types/agendaForm';
import { createRandomString } from '../common';
import { toLowerCamelCase } from '../string';
import { generateGridItems, recalcSectionItems } from './items';
import { getStartPos } from './gridUtils.ts';
import { DEFAULT_TABLE_COLUMN_CONFIGURATION } from './tableColumnConfiguration';

export const getExistingItem = (items: FormItem[], x: number, y: number) => {
  return items.find((item) => {
    const startX = getStartPos(item.x);
    const startY = getStartPos(item.y);
    return startX === x && startY === y;
  });
};

export const mapSection = (section: FormSectionRes): LocalSection => {
  const gridSizeX = section.gridSizeX;
  const gridSizeY = section.gridSizeY;

  const formItemTabs =
    section.formItemTabs?.map(({ tab, items }) => {
      const cells = createItems(gridSizeX, gridSizeY, items);

      return {
        tab: { ...tab },
        items: cells,
      };
    }) ?? [];

  if (!formItemTabs.length) {
    formItemTabs.push(createEmptyFormItemTab(null, { gridSizeX, gridSizeY }));
  }

  return {
    gridSizeX: section.gridSizeX,
    gridSizeY: section.gridSizeY,
    name: section.name,
    description: section.description ?? '',
    collapsed: section.collapsed ?? false,
    id: createRandomString(10),
    items: recalcSectionItems(formItemTabs),
    formItemTabs: formItemTabs,
  };
};

export const mapFormItem = (formItem: FormItem | undefined, x: string, y: string, identifier: string): LocalFormItem => {
  const definitionType = toLowerCamelCase(formItem?.definition.type || '') as keyof FormItemDefinition;
  const definition = formItem?.definition[definitionType];

  return {
    x: formItem?.x || x,
    y: formItem?.y || y,
    identifier: formItem?.name ?? identifier,
    isEmpty: !formItem,
    data: formItem
      ? ({
          id: formItem.id,
          disabledName: true,
          name: formItem.name,
          title: formItem.title,
          translatesTitle: formItem.translatesTitle,
          x: formItem.x,
          y: formItem.y,
          isAuxiliary: formItem.isAuxiliary,
          isActionInput: formItem.isActionInput ?? false,
          isParentInput: formItem?.defaultValue?.valueSourceType === 'ParentRecordId',
          defaultValue: formItem.defaultValue
            ? {
                formItemId: formItem.defaultValue?.formItemId ?? null,
                valueDirect: formItem.defaultValue?.valueDirect ?? null,
                valueSourceType: formItem.defaultValue?.valueSourceType ?? null,
                valueOffsetDays: formItem.defaultValue?.valueOffsetDays ?? null,
              }
            : null,
          definition: {
            type: formItem.definition.type,
            tableColumnConfiguration: { ...(formItem.definition.tableColumnConfiguration ?? DEFAULT_TABLE_COLUMN_CONFIGURATION) },
            [`${definitionType}`]: definition,
          },
          visibilities: formItem.visibilities,
        } satisfies LocalFormItemData)
      : null,
  };
};

export const createEmptyTabSection = (name: string | null = '1'): LocalSection => {
  return {
    gridSizeX: 1,
    gridSizeY: 1,
    name: name,
    description: '',
    collapsed: false,
    id: createRandomString(10),
    items: [
      {
        x: '0',
        y: '0',
        identifier: createRandomString(10),
        isEmpty: true,
        data: null,
      },
    ],
    formItemTabs: [createEmptyFormItemTab(null)] satisfies LocalFormItemTab[],
  };
};

type FormItemTabSectionData = {
  gridSizeX: number;
  gridSizeY: number;
  items?: FormItem[];
};
export const createEmptyFormItemTab = (name: string | null, sectionData?: FormItemTabSectionData): LocalFormItemTab => {
  const gridSizeX = sectionData?.gridSizeX ?? 1;
  const gridSizeY = sectionData?.gridSizeY ?? 1;
  const items = sectionData?.items ?? [];

  const cells = createItems(gridSizeX, gridSizeY, items);

  return {
    tab: { number: 0, name: name },
    items: cells,
  };
};

export const createNewSection = (
  name: string | null = '1',
  gridSizeX: number = 1,
  gridSizeY: number = 1,
  description: string | null = '',
  collapsed: boolean = false
): LocalSection => {
  const itemIdentifier = createRandomString(10);

  return {
    gridSizeX: gridSizeX,
    gridSizeY: gridSizeY,
    name: name,
    description: description,
    collapsed: collapsed,
    id: createRandomString(10),
    items: [
      {
        x: '0',
        y: '0',
        identifier: itemIdentifier,
        isEmpty: true,
        data: null,
      },
    ],
    formItemTabs: [createEmptyFormItemTab(null, { gridSizeX, gridSizeY })],
  };
};

export const createItems = (gridSizeX: number = 1, gridSizeY: number = 1, items?: FormItem[]): LocalFormItem[] => {
  const numberOfCells = gridSizeX * gridSizeY;

  return Array.from({ length: numberOfCells }, (_, index) => {
    const x = index % gridSizeX;
    const y = Math.floor(index / gridSizeX);
    const identifier = createRandomString(10);

    const existingItem = getExistingItem(items || [], x, y);

    return mapFormItem(existingItem, String(x), String(y), identifier);
  });
};

export const findTabIndex = (tabs: WithTab[], subTabNumber: number) => {
  return tabs.findIndex(({ tab }) => tab.number === subTabNumber);
};

export const getFirstFormItemTab = (formItemTabs: LocalFormItemTab[]) =>
  formItemTabs.reduce<LocalFormItemTab | undefined>((first, item) => (!first || item.tab.number < first.tab.number ? item : first), undefined);

export const getFirstSubTabNumber = (formItemTabs: LocalFormItemTab[]) => getFirstFormItemTab(formItemTabs)?.tab.number ?? 0;

export const getSubTabNumberByName = (formItemTabs: LocalFormItemTab[], name?: string | null) =>
  formItemTabs.find((item) => item.tab.name === name)?.tab.number ?? getFirstSubTabNumber(formItemTabs);

const assignFormItemTabNumbers = (formItemTabs: LocalFormItemTab[]): LocalFormItemTab[] =>
  formItemTabs.map((formItemTab, index) => ({
    ...formItemTab,
    tab: { ...formItemTab.tab, number: index },
  }));

export const reindexFormItemTabs = (formItemTabs: LocalFormItemTab[]): LocalFormItemTab[] =>
  assignFormItemTabNumbers([...formItemTabs].sort((a, b) => a.tab.number - b.tab.number));

export const moveFormItemTab = (formItemTabs: LocalFormItemTab[], fromNumber: number, toNumber: number): LocalFormItemTab[] | null => {
  if (fromNumber === toNumber) return null;

  const sortedTabs = [...formItemTabs].sort((a, b) => a.tab.number - b.tab.number);
  const oldIndex = sortedTabs.findIndex((item) => item.tab.number === fromNumber);
  const newIndex = sortedTabs.findIndex((item) => item.tab.number === toNumber);

  if (oldIndex === -1 || newIndex === -1) return null;

  const reordered = [...sortedTabs];
  const [moved] = reordered.splice(oldIndex, 1);
  reordered.splice(newIndex, 0, moved);

  return assignFormItemTabNumbers(reordered);
};

const assignTabSectionNumbers = (tabSections: TabSection[]): TabSection[] =>
  tabSections.map((tabSection, index) => ({
    ...tabSection,
    tab: { ...tabSection.tab, number: index },
  }));

export const moveTabSections = (tabSections: TabSection[], fromNumber: number, toNumber: number): TabSection[] | null => {
  if (fromNumber === toNumber) return null;

  const sortedTabs = [...tabSections].sort((a, b) => a.tab.number - b.tab.number);
  const oldIndex = sortedTabs.findIndex((item) => item.tab.number === fromNumber);
  const newIndex = sortedTabs.findIndex((item) => item.tab.number === toNumber);

  if (oldIndex === -1 || newIndex === -1) return null;

  const [moved] = sortedTabs.splice(oldIndex, 1);
  sortedTabs.splice(newIndex, 0, moved);

  return assignTabSectionNumbers(sortedTabs);
};

export const remapTabNumberAfterMove = (selected: number, fromNumber: number, toNumber: number): number => {
  if (selected === fromNumber) return toNumber;

  if (fromNumber < toNumber && selected > fromNumber && selected <= toNumber) return selected - 1;
  if (fromNumber > toNumber && selected >= toNumber && selected < fromNumber) return selected + 1;

  return selected;
};

export const findSectionIndex = (sections: LocalSection[] | TemplateSection[], sectionName: string | null) => {
  return sections.findIndex((section) => section.name === sectionName);
};

type GridSize = {
  gridSizeX: number;
  gridSizeY: number;
};

export const getMaxGridSize = (a: GridSize, b: GridSize): GridSize => ({
  gridSizeX: Math.max(a.gridSizeX, b.gridSizeX),
  gridSizeY: Math.max(a.gridSizeY, b.gridSizeY),
});

export const needsSubTabMoveLayoutConfirm = (source: GridSize, dest: GridSize): boolean =>
  dest.gridSizeX < source.gridSizeX || dest.gridSizeY < source.gridSizeY;

export const syncFormItemTabsToGrid = (formItemTabs: LocalFormItemTab[], gridSizeX: number, gridSizeY: number): LocalFormItemTab[] =>
  formItemTabs.map((tab) => ({
    ...tab,
    items: generateGridItems(tab.items, gridSizeX, gridSizeY),
  }));
